// Level meter ballistics, pure. Mirrors the app: dsp::LevelMeter (instant
// attack, constant 40 dB/s decay, −100 dB silence floor) feeding
// AudioLevelMeter's peak hold (500 ms) and gravitational drop (400 dB/s²).
// The meter displays −60…0 dBFS.

export const METER_MIN_DB = -60;
export const METER_MAX_DB = 0;
export const METER_SILENCE_DB = -100;
export const METER_DECAY_DB_PER_S = 40;
export const PEAK_HOLD_MS = 500;
export const PEAK_GRAVITY_DB_PER_S2 = 400;

export interface ChannelMeter {
  level: number;    // smoothed level, dB
  peak: number;     // peak-hold indicator, dB
  peakVel: number;  // dB/s, grows while the peak falls
  peakAt: number;   // ms timestamp of the last new peak
}

export const silentChannel = (): ChannelMeter => ({
  level: METER_SILENCE_DB, peak: METER_SILENCE_DB, peakVel: 0, peakAt: 0,
});

export const peakToDb = (linearPeak: number): number =>
  linearPeak > 0 ? Math.max(METER_SILENCE_DB, 20 * Math.log10(linearPeak)) : METER_SILENCE_DB;

// 0 at the left (−60 dB) … 1 at the right (0 dB).
export const dbToFrac = (db: number): number =>
  Math.min(1, Math.max(0, (db - METER_MIN_DB) / (METER_MAX_DB - METER_MIN_DB)));

export function stepChannel(ch: ChannelMeter, inputDb: number, nowMs: number, dtMs: number): ChannelMeter {
  const dtS = Math.max(0, dtMs) / 1000;
  // Instant attack, constant dB/sec decay, never below the input or silence.
  const level = inputDb > ch.level
    ? inputDb
    : Math.max(ch.level - METER_DECAY_DB_PER_S * dtS, inputDb, METER_SILENCE_DB);

  if (level > ch.peak) {
    return { level, peak: level, peakVel: 0, peakAt: nowMs };
  }
  if (nowMs - ch.peakAt > PEAK_HOLD_MS) {
    const peakVel = ch.peakVel + PEAK_GRAVITY_DB_PER_S2 * dtS;
    const peak = Math.max(ch.peak - peakVel * dtS, METER_SILENCE_DB);
    return { level, peak, peakVel, peakAt: ch.peakAt };
  }
  return { ...ch, level };
}

// Nothing left to animate: both the bar and the peak are off the scale.
export const isSettled = (ch: ChannelMeter): boolean =>
  ch.level <= METER_MIN_DB && ch.peak <= METER_MIN_DB;
